<template>
  <div class="container rounded py-4">
    <v-row class="app-invoice-preview">
      <v-col cols="12">
        <v-card>
          <!-- Header -->
          <v-card-text class="px-4">
            <div class="invoice-header d-flex justify-space-between">
              <!-- Left Content -->
              <span class="font-weight-semibold">Protocolo: {{ ticketData.protocolo }}</span>

              <span class="font-weight-semibold">Data de Abertura: {{ functions.converterDateTimeStringEmLocaleDateTimeString(ticketData.dataCadastro) }}</span>

              <span class="font-weight-semibold">Tipo Solicitação: {{ ticketData.tipoSolicitacaoNome ? ticketData.tipoSolicitacaoNome : 'Nenhum tipo selecionado' }}</span>
              <span class="font-weight-semibold">Status: {{ ticketData.statusNome }}</span>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-text class="py-4 px-8">
            <h3 class="py-2 font-weight-bold text-xl text--primary">
              Dados do solicitante
            </h3>

            <div class="payment-details d-flex justify-space-between flex-wrap flex-column flex-sm-row">
              <div class="mb-8 mb-sm-0">
                <p class="mb-2">
                  <span>Nome: </span>
                  <span class="font-weight-semibold">{{ ticketData.solicitante.nome }}</span>
                </p>

                <p class="mb-2">
                  <span>CPF: </span>
                  <span class="font-weight-semibold">{{ ticketData.solicitante.cpf }}</span>
                </p>

                <p class="mb-2">
                  <span>Data de nascimento: </span>
                  <span class="font-weight-semibold">{{ ticketData.solicitante.dataNascimento }}</span>
                </p>

                <div v-if="ticketData.solicitante.endereco">
                  <p class="mb-2">
                    <span>Endereço: </span>

                    <span class="font-weight-semibold">{{ ticketData.solicitante.endereco.rua }}, {{ ticketData.solicitante.endereco.numero }} </span>

                    <span class="font-weight-semibold"> {{ ticketData.solicitante.endereco.cidade }} {{ ticketData.solicitante.endereco.estado }}, {{ ticketData.solicitante.endereco.bairro }}, {{ ticketData.solicitante.endereco.cep }} </span>
                  </p>
                </div>

                <div v-else>
                  <p class="mb-2">
                    <span>Endereço: </span>
                  </p>
                </div>
              </div>

              <div class="mb-8 mb-sm-0">
                <p class="mb-2">
                  <span>E-mail: </span>
                  <span class="font-weight-semibold">{{ solicitanteEmail }}</span>
                </p>

                <p class="mb-2">
                  <span>Orgão emissor: </span>
                  <span class="font-weight-semibold">{{ ticketData.solicitante.orgaoEmissor }}</span>
                </p>
              </div>

              <div>
                <p class="mb-2">
                  <span>Celular: </span>
                  <span class="font-weight-semibold">
                    {{ functions.formatarCelular(solicitanteCelular) }}
                  </span>
                </p>

                <p class="mb-2">
                  <span>RG: </span>
                  <span class="font-weight-semibold">{{ ticketData.solicitante.rg }}</span>
                </p>
              </div>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-text class="px-8 py-6">
            <h3 class="py-2 font-weight-bold text-xl text--primary">
              Mensagens - Atendente: {{ atendenteNome }}
            </h3>

            <chat-pdf
              :solicitante-nome="ticketData.solicitante.nome"
              :solicitante-email="solicitanteEmail"
              :ticket-id="ticketSelecionadoId"
              :mensagens="mensagens"
              :ticket-esta-finalizado="false"
            />
          </v-card-text>

          <v-divider></v-divider>

          <v-expansion-panels
            :value="expansionPanelsModel"
            multiple
            focusable
          >
            <template v-for="(item, index) in secoesPersonalizadas">
              <v-expansion-panel :key="index">
                <v-expansion-panel-header>
                  <h3 class="py-2 font-weight-bold text-no-wrap">
                    {{ item.titulo }}
                  </h3>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <secao-configurada-pdf
                    :editavel="item.editavelAgente"
                    :elementos="item.elementos"
                    :linhas="item.linhas"
                    :colunas="item.colunas"
                    :ticket="ticketData"
                    :formulario-id="item.idFormulario"
                    @atualizar-respostas="formulariosRespostas = $event"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </template>
          </v-expansion-panels>

          <v-divider></v-divider>

          <historico-pdf />
        </v-card>
      </v-col>
    </v-row>

    <v-btn
      class="button-print mt-5"
      color="success"
      @click="gerarPdf"
    >
      Gerar arquivo PDF
    </v-btn>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import SecaoConfiguradaPdf from './SecaoConfiguradaPdf.vue'
import HistoricoPdf from './HistoricoPdf.vue'
import ChatPdf from './ChatPdf.vue'
import useTicketMensagens from '@/composables/useTicketMensagens'
import useAtendimentoTicket from '@/composables/useAtendimentoTicket'
import useUserStore from '@/store/user'
import useAtendimentoStore from '@/store/atendimento'
import { formatarCelular } from '@/utils/formatting'
import { converterDateTimeStringEmLocaleDateTimeString } from '@/utils/dateTime'

export default {
  components: {
    SecaoConfiguradaPdf,
    HistoricoPdf,
    ChatPdf,
  },

  props: {
    getTickets: {
      type: Function,
      required: true,
    },
    getTicketsAndSelectFirst: {
      type: Function,
      required: true,
    },
  },

  setup(props) {
    const { ticketSelecionadoId } = storeToRefs(useAtendimentoStore())

    const {
      ticketData,
      secoesPersonalizadas,
      formulariosRespostas,
    } = useAtendimentoTicket(ticketSelecionadoId)

    const { mensagens, getMensagens } = useTicketMensagens(ticketSelecionadoId)

    watch(ticketSelecionadoId, async () => {
      await getMensagens()
      props.getTickets()
    })

    const loading = ref(false)

    const expansionPanelsModel = ref([])

    const { nome: atendenteNome } = storeToRefs(useUserStore())

    const gerarPdf = () => {
      window.print()
    }

    const solicitanteEmail = computed(() => ticketData.value.solicitante.contatos?.find(c => !!c.email)?.email)
    const solicitanteCelular = computed(() => ticketData.value.solicitante.contatos?.find(c => !!c.celular)?.celular)

    return {
      loading,
      expansionPanelsModel,
      atendenteNome,
      ticketData,
      secoesPersonalizadas,
      formulariosRespostas,
      ticketSelecionadoId,
      mensagens,
      gerarPdf,
      solicitanteEmail,
      solicitanteCelular,
      functions: {
        converterDateTimeStringEmLocaleDateTimeString,
        formatarCelular,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/apps/invoice.scss';

@media print {
  .v-application {
    background: none !important;
  }

  .app-navigation-menu,
  .v-app-bar,
  .listagem-tickets,
  .tab-atendimento,
  .button-print,
  .v-footer {
    display: none !important;
  }

  .v-main,
  .app-content-container,
  .app-navigation-menu {
    padding: 0px !important;
  }

  .v-card {
    box-shadow: none !important;
  }

  .app-invoice-preview {
    .invoice-header,
    .payment-details,
    .invoice-total {
      &.d-flex {
        flex-direction: row !important;
        width: 100% !important;
        flex: 1 !important;
      }
    }
  }
}
</style>
